/**
 * KawaiiConfetti — purely-decorative overlay of tiny hearts, stars, and
 * sparkles drifting around the menu board. Keeps the page feeling alive
 * for younger guests without distracting from the actual menu.
 *
 * The SVG glyphs are inline so we don't pull in an icon font; positions
 * + animation delays are seeded from a fixed list so the layout is
 * stable across renders (no flicker every 15s on the React Query poll).
 */
import { memo } from "react";

const HEART = (
  <path
    d="M12 21s-7-4.5-9.5-9C.8 8.7 2.5 5 6 5c2 0 3.5 1.5 4 2.5C10.5 6.5 12 5 14 5c3.5 0 5.2 3.7 3.5 7-2.5 4.5-9.5 9-9.5 9z"
    transform="translate(-12 -12)"
  />
);
const STAR = (
  <path
    d="M12 2l2.6 6.3 6.8.5-5.2 4.5 1.6 6.6L12 16.7 6.2 19.9l1.6-6.6-5.2-4.5 6.8-.5z"
    transform="translate(-12 -12)"
  />
);
const SPARKLE = (
  <path
    d="M12 2 L13 10 L21 12 L13 14 L12 22 L11 14 L3 12 L11 10 Z"
    transform="translate(-12 -12)"
  />
);

const PIECES = [
  { kind: "heart",   x: 8,  y: 12, size: 22, color: "#ec4899", d: 0.0,  rot: -12 },
  { kind: "star",    x: 18, y: 36, size: 18, color: "#fbbf24", d: 1.4,  rot: 14 },
  { kind: "sparkle", x: 6,  y: 62, size: 16, color: "#a78bfa", d: 2.2,  rot: 0 },
  { kind: "heart",   x: 22, y: 84, size: 18, color: "#fda4af", d: 1.0,  rot: 22 },
  { kind: "star",    x: 92, y: 8,  size: 24, color: "#60a5fa", d: 0.6,  rot: -18 },
  { kind: "sparkle", x: 88, y: 32, size: 16, color: "#fbbf24", d: 2.6,  rot: 8 },
  { kind: "heart",   x: 94, y: 56, size: 16, color: "#ec4899", d: 1.8,  rot: -8 },
  { kind: "star",    x: 80, y: 80, size: 22, color: "#a78bfa", d: 3.0,  rot: 30 },
  { kind: "sparkle", x: 50, y: 96, size: 14, color: "#fbbf24", d: 1.2,  rot: 0 },
  { kind: "heart",   x: 38, y: 6,  size: 14, color: "#fda4af", d: 2.4,  rot: 16 },
  { kind: "star",    x: 64, y: 4,  size: 16, color: "#60a5fa", d: 0.4,  rot: -6 },
  { kind: "sparkle", x: 30, y: 50, size: 12, color: "#a78bfa", d: 3.2,  rot: 20 },
  { kind: "heart",   x: 70, y: 50, size: 14, color: "#ec4899", d: 0.8,  rot: -22 },
  { kind: "star",    x: 12, y: 96, size: 12, color: "#fbbf24", d: 2.0,  rot: 10 },
  { kind: "sparkle", x: 96, y: 90, size: 14, color: "#a78bfa", d: 1.6,  rot: 4 },
];

function Glyph({ kind }) {
  if (kind === "heart") return HEART;
  if (kind === "star") return STAR;
  return SPARKLE;
}

function KawaiiConfetti({ className = "" }) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {PIECES.map((p, i) => (
        <span
          key={i}
          className="absolute block"
          style={{ left: `${p.x}%`, top: `${p.y}%`, transform: `rotate(${p.rot}deg)` }}
        >
          <svg
            viewBox="0 0 24 24"
            width={p.size}
            height={p.size}
            fill={p.color}
            className="block bp-twinkle"
            style={{
              filter: `drop-shadow(0 0 9px ${p.color})`,
              animationDelay: `${p.d}s`,
              animationDuration: `${2.4 + (i % 4) * 0.5}s`,
            }}
          >
            <Glyph kind={p.kind} />
          </svg>
        </span>
      ))}
    </div>
  );
}

export default memo(KawaiiConfetti);
